import type { ContractType } from '../../types/agreement'
import type { ContractScopeType } from '../../api/turnoverContracts'

interface PropertyOption {
  id: string
  customer_id?: string | null
  name?: string | null
  address?: string | null
}

interface Props {
  contractType: ContractType | ''
  customerId: string
  scopeType: ContractScopeType
  selectedPropertyIds: string[]
  properties: PropertyOption[]
  onScopeChange: (scope: ContractScopeType) => void
  onSelectionChange: (propertyIds: string[]) => void
  onCreateProperty?: () => void
}

export function ContractPropertyScopeFields({
  contractType,
  customerId,
  scopeType,
  selectedPropertyIds,
  properties,
  onScopeChange,
  onSelectionChange,
  onCreateProperty,
}: Props) {
  const supportsMultiple = contractType === 'one_time' || contractType === 'as_needed'
  const customerProperties = properties.filter((p) => p.customer_id === customerId)

  const toggleProperty = (propertyId: string) => {
    if (scopeType === 'single_property') {
      onSelectionChange([propertyId])
      return
    }
    onSelectionChange(
      selectedPropertyIds.includes(propertyId)
        ? selectedPropertyIds.filter((id) => id !== propertyId)
        : [...selectedPropertyIds, propertyId]
    )
  }

  return (
    <div className="space-y-3">
      {supportsMultiple && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Contract coverage</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onScopeChange('single_property')}
              className={`text-left rounded-lg border px-3 py-2 text-sm ${
                scopeType === 'single_property'
                  ? 'border-blue-500 bg-blue-50 text-blue-800'
                  : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              <span className="font-medium block">One building</span>
              <span className="text-xs">This contract belongs to one property.</span>
            </button>
            <button
              type="button"
              onClick={() => onScopeChange('customer_properties')}
              className={`text-left rounded-lg border px-3 py-2 text-sm ${
                scopeType === 'customer_properties'
                  ? 'border-blue-500 bg-blue-50 text-blue-800'
                  : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              <span className="font-medium block">Multiple customer buildings</span>
              <span className="text-xs">Add or remove covered buildings over time.</span>
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            {scopeType === 'customer_properties' ? 'Covered buildings *' : 'Property *'}
          </label>
          {onCreateProperty && (
            <button
              type="button"
              onClick={onCreateProperty}
              disabled={!customerId}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
            >
              + New property
            </button>
          )}
        </div>
        {!customerId ? (
          <p className="text-sm text-gray-500">Select a customer first.</p>
        ) : customerProperties.length === 0 ? (
          <p className="text-sm text-gray-500">This customer has no properties yet.</p>
        ) : scopeType === 'single_property' ? (
          <select
            required
            value={selectedPropertyIds[0] || ''}
            onChange={(e) => onSelectionChange(e.target.value ? [e.target.value] : [])}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select property...</option>
            {customerProperties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name || property.address}
              </option>
            ))}
          </select>
        ) : (
          <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-300 divide-y divide-gray-200 bg-white">
            {customerProperties.map((property) => (
              <label
                key={property.id}
                className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedPropertyIds.includes(property.id)}
                  onChange={() => toggleProperty(property.id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">
                    {property.name || 'Unnamed property'}
                  </span>
                  <span className="block text-xs text-gray-500 truncate">{property.address}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        {scopeType === 'customer_properties' && (
          <p className="text-xs text-gray-500 mt-1">
            {selectedPropertyIds.length} building{selectedPropertyIds.length === 1 ? '' : 's'} covered.
            You can change this list after the contract starts.
          </p>
        )}
      </div>
    </div>
  )
}
